import { isDevMode } from '@angular/core';
import { RowLinkConfig, TableConfig } from '../model/ColumnConfig';
import { TableRow } from '../model/TableRow';
import { isFeatureEnabled } from '../utils/feature-enabled.util';

/**
 * Провайдер ссылочной строки (bodyRowCfg.rowLinkCfg). Считает набор навигационных
 * data-колонок и «первую» нав-колонку (единственный tab-стоп для a11y), резолвит
 * routerLink/state/queryParams/aria-label от row.rowSrc. Колонку НЕ добавляет —
 * декорирует существующие data-ячейки (шаблон оборачивает их в <a routerLink>).
 */
export class RowLinkProvider<T> {
  public readonly isEnabled: boolean = true;

  private navKeys = new Set<string>();
  private _primaryKey: string | null = null;

  constructor(
    private readonly cfg: RowLinkConfig<T>,
    private readonly columnKeys: string[],
  ) {}

  /**
   * Пересчитывает навигационные ключи и первую нав-колонку.
   * Вызывается там же, где меняются входные (prepareTableData, rebuildCellTemplates).
   * @param displayColumns порядок колонок — для выбора первой нав-колонки
   * @param cellTemplateKeys ключи колонок с ngxAurCellDef (кастомные ячейки)
   */
  public init(displayColumns: string[], cellTemplateKeys: Set<string>): this {
    const exclude = new Set(this.cfg.excludeColumns ?? []);
    const include = new Set(this.cfg.includeColumns ?? []);
    const columnSet = new Set(this.columnKeys);

    this.navKeys = new Set<string>();
    for (const key of this.columnKeys) {
      if (exclude.has(key)) continue;                     // exclude выигрывает
      const isBuiltinData = !cellTemplateKeys.has(key);   // нет ngxAurCellDef для ключа
      if (isBuiltinData || include.has(key)) this.navKeys.add(key);
    }
    this._primaryKey = displayColumns.find(k => this.navKeys.has(k)) ?? null;

    if (isDevMode()) {
      include.forEach(key => {
        if (!columnSet.has(key)) {
          console.warn(`[aur-mat-table] rowLinkCfg.includeColumns: колонка "${key}" не найдена в columnsCfg — игнорируется.`);
        }
      });
      if (this._primaryKey === null) {
        console.warn('[aur-mat-table] rowLinkCfg задан, но навигационных колонок нет (все исключены или кастомные без includeColumns) — строки не будут ссылочными.');
      }
    }
    return this;
  }

  /** Колонка навигационная (столбцовый признак; от строки не зависит). */
  public isNav(key: string): boolean {
    return this.navKeys.has(key);
  }

  /** Первая навигационная колонка в порядке отображения (единственный tab-стоп). */
  public isPrimary(key: string): boolean {
    return this._primaryKey === key;
  }

  /** У строки есть активная ссылка (routerLink(row) непустой). */
  public hasLink(row: TableRow<T>): boolean {
    const t = this.cfg.routerLink(row);
    if (t == null) return false;
    if (Array.isArray(t) && t.length === 0) return false;
    return true;
  }

  /** Строка реально рендерит ссылку: есть навигационная колонка (primaryKey) И активный routerLink. */
  public isLinkRow(row: TableRow<T>): boolean {
    return this._primaryKey !== null && this.hasLink(row);
  }

  /** Нормализованные RouterLink-команды (вызывать только когда hasLink === true). */
  public target(row: TableRow<T>): any[] | string {
    return this.cfg.routerLink(row) as any[] | string;
  }

  /** Router state строки (вычисляется от row.rowSrc); undefined, если state не задан в rowLinkCfg. */
  public state(row: TableRow<T>): Record<string, any> | undefined {
    return this.cfg.state?.(row);
  }

  /** queryParams строки (вычисляется от row.rowSrc); undefined, если queryParams не задан. */
  public queryParams(row: TableRow<T>): Record<string, any> | undefined {
    return this.cfg.queryParams?.(row);
  }

  /** aria-label ссылки строки (вычисляется от row.rowSrc); undefined, если ariaLabel не задан. */
  public ariaLabel(row: TableRow<T>): string | undefined {
    return this.cfg.ariaLabel?.(row);
  }

  public static create<T>(tableConfig: TableConfig<T>): RowLinkProvider<T> {
    const cfg = tableConfig.bodyRowCfg?.rowLinkCfg;
    if (isFeatureEnabled(cfg)) {
      return new RowLinkProvider<T>(cfg as RowLinkConfig<T>, tableConfig.columnsCfg.map(c => c.key));
    }
    return new RowLinkProviderDummy<T>();
  }
}

/** Заглушка: таблица без rowLinkCfg работает через неё (паттерн как у прочих провайдеров). */
export class RowLinkProviderDummy<T> extends RowLinkProvider<T> {
  public override readonly isEnabled = false;

  constructor() {
    super({ routerLink: () => null }, []);
  }

  public override init(_displayColumns: string[], _cellTemplateKeys: Set<string>): this {
    return this;
  }
  public override isNav(_key: string): boolean {
    return false;
  }
  public override isPrimary(_key: string): boolean {
    return false;
  }
  public override hasLink(_row: TableRow<T>): boolean {
    return false;
  }
  public override isLinkRow(_row: TableRow<T>): boolean {
    return false;
  }
}
